import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TabNavigationService } from '~/core/services/tab-navigation.service';
import { BacklogService } from '~/core/services/backlog.service';
import { AuthService } from '~/core/services/auth.service';
import { AppSettingsService } from '~/core/services/appsettings.service';
import { WellsApiService } from '~/core/services/msal_token.service';
import { HttpService, EmailVolumeByDay, EmailByDayOfWeek, VolumeTrendItem, ClassificationSplitItem, TemplateDistributionItem, CounterpartyNegotiationItem, EscalationDistributionItem, AgingQueueItem, EscalationByCounterpartyItem, FrictionClausesItem, EscalationTrendItem, EscalationCategoriesItem } from '~/core/services/http.service';

export interface MailboxSender {
    name: string;
    email: string;
    mailsSent: number;
    lastSeen: string;
}

@Component({
    selector: 'app-bu-selection',
    templateUrl: './bu-selection.component.html',
    styleUrls: ['./bu-selection.component.scss'],
    standalone: false
})
export class BuSelectionComponent implements OnInit {
    // UI State
    public showWorkIdAlert: boolean = false;
    public showMailboxSenders: boolean = true;
    public activeTab: string = 'legal';
    public isLoggedIn: boolean = false;
    public userRole: string = '';

    // Authentication State
    public isAuthenticated: boolean = false;
    public isLoading: boolean = true;
    public isLoadingKPIs: boolean = true;
    public isLoadingEmailVolume: boolean = true;
    public isLoadingAnalytics: boolean = true;
    public error: string = '';

    private kpiSummaryLoaded: boolean = false;
    private senderKpisLoaded: boolean = false;
    private volumeAnalyticsLoaded: boolean = false;
    private riskAnalyticsLoaded: boolean = false;
    private dashboardLoadId: number = 0;

    // Filter and Dates
    public fromDate: string = '2026-09-03';
    public toDate: string = '';
    public selectedCounterparty: string = '';
    public counterparties: string[] = [];
    public subjects: string[] = [];
    public classifications: string[] = [];
    public escalations: string[] = [];

    // Track user interactions with filters
    public userSelectedFromDate: boolean = false;
    public userSelectedToDate: boolean = false;
    public userSelectedCounterparty: boolean = false;

    // KPI Cards Data
    public kpis = {
        ndaMailsProcessed: 0,
        newNdaRequests: 0,
        reviewNda: 0,
        outOfScope: 0,
        hardEscalations: 0,
        noEscalation: 0,
        avgProcessingTime: '--',
        uniqueSenders: 0,
        avgMailsPerSender: '--'
    };

    // Mailbox Senders Table Data
    public mailboxSenders: MailboxSender[] = [];

    // Email Volume and Day of Week Graph Data
    public emailVolumeByDay: EmailVolumeByDay[] = [];
    public emailsByDayOfWeek: EmailByDayOfWeek[] = [];

    // Volume Analytics Graph Data
    public volumeTrend: VolumeTrendItem[] = [];
    public classificationSplit: ClassificationSplitItem[] = [];
    public templateDistribution: TemplateDistributionItem[] = [];
    public counterpartyNegotiations: CounterpartyNegotiationItem[] = [];

    // Risk Analytics Graph Data
    public escalationDistribution: EscalationDistributionItem[] = [];
    public agingQueue: AgingQueueItem[] = [];
    public escalationByCounterparty: EscalationByCounterpartyItem[] = [];
    public frictionClauses: FrictionClausesItem[] = [];
    public escalationTrend: EscalationTrendItem[] = [];
    public escalationCategories: EscalationCategoriesItem[] = [];

    constructor(
        private router: Router,
        private tabNavService: TabNavigationService,
        private backlogService: BacklogService,
        private httpService: HttpService,
        private authService: AuthService,
        private appSettingsService: AppSettingsService,
        private wellsApiService: WellsApiService
    ) {}

    ngOnInit() {
        this.toDate = this.getDateString(0);

        // Check if user is logged in
        this.isLoggedIn = this.authService.isAuthenticated;
        const currentUser = this.authService.currentUserValue;
        if (currentUser) {
            this.userRole = currentUser.role;
        }

        // Authenticate with Wells API before loading dashboard data
        this.authenticateWithWellsAPI();
    }

    //*************************************************************************
    //  Authentication Methods
    //*************************************************************************
    /**
     * Authenticate with Wells API using MSAL token
     * Acquires Wells API token and stores it for subsequent API calls
     * Loads dashboard data in parallel - doesn't wait for token
     */
    public async authenticateWithWellsAPI() {
        try {
            this.isLoading = true;
            this.error = "";
            
            // Get user email from app settings
            const settings = await this.appSettingsService.getSettings();
            const email = settings?.user?.username || settings?.user?.email || 'developer@chevron.com';
            
            console.log('[BuSelectionComponent] authenticateWithWellsAPI - Starting authentication and data load');

            // Acquire token first to avoid firing dashboard calls with missing auth headers.
            this.wellsApiService.issueDevToken(email).subscribe({
                next: (response: any) => {
                    console.log('[BuSelectionComponent] authenticateWithWellsAPI - Token acquired successfully');
                    this.isAuthenticated = true;
                    this.error = "";

                    // Load dashboard only after token is available.
                    this.loadDashboardData();
                    this.isLoading = false;
                },
                error: (error: any) => {
                    console.error('[BuSelectionComponent] authenticateWithWellsAPI - Token acquisition failed:', error);
                    this.error = `Token acquisition warning: ${error.message || 'Unknown error'}`;
                    // Fallback: still attempt dashboard load (cached token may exist).
                    this.isAuthenticated = false;

                    this.loadDashboardData();
                    this.isLoading = false;
                }
            });

        } catch (error: any) {
            console.error('[BuSelectionComponent] authenticateWithWellsAPI - Error during authentication:', error);
            this.error = `Authentication error: ${error.message}`;
            this.isAuthenticated = false;
            this.isLoading = false;
        }
    }

    /**
     * Load all dashboard data after successful authentication
     * Loads filters, KPIs, senders, volume, and risk data
     */
    private loadDashboardData(): void {
        console.log('[BuSelectionComponent] loadDashboardData - Loading all dashboard data');
        this.beginDashboardLoad();
        
        // Load filters and KPI data
        this.loadFilters();
        this.loadKPIData();
        this.loadMailboxSendersData();
        this.loadVolumeData();
        this.loadRiskData();
        
        this.isLoading = false;
    }

    private beginDashboardLoad(): void {
        this.dashboardLoadId++;
        this.kpiSummaryLoaded = false;
        this.senderKpisLoaded = false;
        this.volumeAnalyticsLoaded = false;
        this.riskAnalyticsLoaded = false;
        this.isLoadingKPIs = true;
        this.isLoadingEmailVolume = true;
        this.isLoadingAnalytics = true;
    }

    /**
     * Called when form filters change - refreshes all dashboard data with new filters
     */
    public onFiltersChanged(): void {
        console.log('[BuSelectionComponent] Filters changed - Refreshing data', {
            fromDate: this.fromDate,
            toDate: this.toDate,
            counterparty: this.selectedCounterparty
        });
        
        this.beginDashboardLoad();
        this.loadKPIData();
        this.loadMailboxSendersData();
        this.loadVolumeData();
        this.loadRiskData();
    }

    /**
     * Called when from date is changed
     */
    public onFromDateChanged(): void {
        this.userSelectedFromDate = true;
        this.onFiltersChanged();
    }

    /**
     * Called when to date is changed
     */
    public onToDateChanged(): void {
        this.userSelectedToDate = true;
        this.onFiltersChanged();
    }

    /**
     * Called when counterparty is changed
     */
    public onCounterpartyChanged(): void {
        this.userSelectedCounterparty = true;
        this.onFiltersChanged();
    }

    /**
     * Clear from date filter
     */
    public clearFromDate(): void {
        this.fromDate = '2026-09-03';
        this.userSelectedFromDate = false;
        this.onFiltersChanged();
    }

    /**
     * Clear to date filter
     */
    public clearToDate(): void {
        this.toDate = this.getDateString(0);
        this.userSelectedToDate = false;
        this.onFiltersChanged();
    }

    /**
     * Clear counterparty filter
     */
    public clearCounterparty(): void {
        this.selectedCounterparty = '';
        this.userSelectedCounterparty = false;
        this.onFiltersChanged();
    }

    /**
     * Clear all filters at once
     */
    public clearAllFilters(): void {
        this.fromDate = '2026-09-03';
        this.toDate = this.getDateString(0);
        this.selectedCounterparty = '';
        this.userSelectedFromDate = false;
        this.userSelectedToDate = false;
        this.userSelectedCounterparty = false;
        this.onFiltersChanged();
    }

    /**
     * Hide WorkID alert
     */
    public hideAlert(): void {
        this.showWorkIdAlert = false;
    }

    /**
     * Toggle mailbox senders section
     */
    public toggleMailboxSenders(): void {
        this.showMailboxSenders = !this.showMailboxSenders;
    }

    /**
     * Select tab (Legal or Developer)
     */
    public selectTab(tab: string): void {
        console.log('[BuSelectionComponent] selectTab - Switching to tab:', tab);
        this.activeTab = tab;
        
        // Set loading states
        this.isLoadingKPIs = true;
        this.isLoadingEmailVolume = true;
        this.isLoadingAnalytics = true;
        
        // Reset filters when switching tabs
        this.fromDate = '2026-09-03';
        this.toDate = this.getDateString(0);
        this.selectedCounterparty = '';
        this.userSelectedFromDate = false;
        this.userSelectedToDate = false;
        this.userSelectedCounterparty = false;
        
        // Reset all data
        this.resetAllData();
        
        // Reload all dashboard data for the new tab
        console.log('[BuSelectionComponent] selectTab - Reloading data for tab:', tab);
        this.loadDashboardData();
    }

    /**
     * Reset all dashboard data
     */
    private resetAllData(): void {
        // Reset KPIs
        this.kpis = {
            ndaMailsProcessed: 0,
            newNdaRequests: 0,
            reviewNda: 0,
            outOfScope: 0,
            hardEscalations: 0,
            noEscalation: 0,
            avgProcessingTime: '--',
            uniqueSenders: 0,
            avgMailsPerSender: '--'
        };
        
        // Reset data arrays
        this.mailboxSenders = [];
        this.emailVolumeByDay = [];
        this.emailsByDayOfWeek = [];
        this.volumeTrend = [];
        this.classificationSplit = [];
        this.templateDistribution = [];
        this.counterpartyNegotiations = [];
        this.escalationDistribution = [];
        this.agingQueue = [];
        this.escalationByCounterparty = [];
        this.frictionClauses = [];
        this.escalationTrend = [];
        this.escalationCategories = [];
    }

    /**
    * Helper method to get date string in YYYY-MM-DD format
    * @param daysAgo Number of days before today (0 = today)
     */
    private getDateString(daysAgo: number): string {
        const date = new Date();
        date.setDate(date.getDate() - daysAgo);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Load filter options from API (counterparties, subjects, classifications, escalations)
     */
    private loadFilters(): void {
        this.httpService.getFilters().subscribe({
            next: (filters) => {
                console.log('[BuSelectionComponent] Filters loaded successfully');
                this.counterparties = filters.counterparties;
                this.subjects = filters.subjects;
                this.classifications = filters.classifications;
                this.escalations = filters.escalations;
            },
            error: (error) => {
                console.error('[BuSelectionComponent] Error loading filters:', error);
                // Provide fallback empty arrays
                this.counterparties = [];
                this.subjects = [];
                this.classifications = [];
                this.escalations = [];
            }
        });
    }

    /**
     * Load KPI data from API
     */
    private loadKPIData(): void {
        this.isLoadingKPIs = true;
        const loadId = this.dashboardLoadId;
        // Only include dates if user has selected them
        const startDate = this.userSelectedFromDate ? this.formatDateForApi(this.fromDate) : '';
        const endDate = this.userSelectedToDate ? this.formatDateForApi(this.toDate) : '';
        const counterparty = this.selectedCounterparty || undefined;

        this.httpService.getDashboardSummary(startDate, endDate, counterparty).subscribe({
            next: (summary) => {
                if (loadId !== this.dashboardLoadId) return;
                console.log('[BuSelectionComponent] Dashboard summary loaded successfully');
                // Map API response to KPI values
                this.kpis.ndaMailsProcessed = summary.ndasInFlight;
                this.kpis.newNdaRequests = summary.newNDARequests;
                this.kpis.reviewNda = summary.reviewNDARequests;
                this.kpis.hardEscalations = summary.hardReviewCount;
                this.kpis.avgProcessingTime = `${summary.avgInboxToDraftHours.toFixed(2)}h`;
                this.kpis.uniqueSenders = summary.mailChains;
                this.kpis.avgMailsPerSender = `${summary.automatedPct.toFixed(1)}%`;
                this.kpiSummaryLoaded = true;
                this.isLoadingKPIs = !(this.kpiSummaryLoaded && this.senderKpisLoaded);
            },
            error: (error) => {
                if (loadId !== this.dashboardLoadId) return;
                console.error('[BuSelectionComponent] Error loading KPI data:', error);
                // KPIs already initialized with 0/default values
                this.kpiSummaryLoaded = true;
                this.isLoadingKPIs = !(this.kpiSummaryLoaded && this.senderKpisLoaded);
            }
        });
    }

    /**
     * Format date string from YYYY/MM/DD to YYYY-MM-DD for API
     */
    private formatDateForApi(dateStr: string): string {
        if (!dateStr) return '';
        return dateStr.replace(/\//g, '-');
    }

    /**
     * Load mailbox senders data including email volume and day of week statistics
     */
    private loadMailboxSendersData(): void {
        this.isLoadingEmailVolume = true;
        const loadId = this.dashboardLoadId;
        // Only include dates if user has selected them
        const startDate = this.userSelectedFromDate ? this.formatDateForApi(this.fromDate) : '';
        const endDate = this.userSelectedToDate ? this.formatDateForApi(this.toDate) : '';
        const counterparty = this.selectedCounterparty || undefined;

        this.httpService.getDashboardLegalSenders(startDate, endDate, counterparty).subscribe({
            next: (response) => {
                if (loadId !== this.dashboardLoadId) return;
                console.log('[BuSelectionComponent] Dashboard legal senders data loaded successfully');
                
                // Populate mailbox senders table data - map API response to table format
                this.mailboxSenders = response.senders.map(sender => ({
                    name: sender.name,
                    email: sender.email,
                    mailsSent: sender.count,
                    lastSeen: this.formatDateFromISO(sender.lastSeen)
                }));

                // Store email volume by day for graphs
                this.emailVolumeByDay = response.emailVolumeByDay;

                // Store emails by day of week for graphs
                this.emailsByDayOfWeek = response.emailsByDayOfWeek;

                // Update KPI cards with aggregate data
                this.kpis.uniqueSenders = response.uniqueCount;
                this.kpis.avgMailsPerSender = response.avgMailsPerSender.toFixed(1);
                this.senderKpisLoaded = true;
                this.isLoadingKPIs = !(this.kpiSummaryLoaded && this.senderKpisLoaded);
                this.isLoadingEmailVolume = false;
            },
            error: (error) => {
                if (loadId !== this.dashboardLoadId) return;
                console.error('[BuSelectionComponent] Error loading mailbox senders data:', error);
                // Keep existing empty/default values
                this.senderKpisLoaded = true;
                this.isLoadingKPIs = !(this.kpiSummaryLoaded && this.senderKpisLoaded);
                this.isLoadingEmailVolume = false;
            }
        });
    }

    /**
     * Format ISO date string to display format (YYYY-MM-DD)
     */
    private formatDateFromISO(isoDate: string): string {
        if (!isoDate) return '';
        // Extract just the date part from ISO format
        return isoDate.split('T')[0];
    }

    /**
     * Load volume analytics data for volume charts
     */
    private loadVolumeData(): void {
        this.isLoadingAnalytics = true;
        const loadId = this.dashboardLoadId;
        // Only include dates if user has selected them
        const startDate = this.userSelectedFromDate ? this.formatDateForApi(this.fromDate) : '';
        const endDate = this.userSelectedToDate ? this.formatDateForApi(this.toDate) : '';
        const counterparty = this.selectedCounterparty || undefined;

        this.httpService.getDashboardLegalVolume(startDate, endDate, counterparty).subscribe({
            next: (response) => {
                if (loadId !== this.dashboardLoadId) return;
                console.log('[BuSelectionComponent] Dashboard legal volume data loaded successfully');
                
                // Store volume trend data
                this.volumeTrend = response.volumeTrend;
                
                // Store classification split data
                this.classificationSplit = response.classificationSplit;
                
                // Store template distribution data
                this.templateDistribution = response.templateDistribution;
                
                // Store counterparty negotiations data
                this.counterpartyNegotiations = response.counterpartyNegotiations;
                this.volumeAnalyticsLoaded = true;
                this.isLoadingAnalytics = !(this.volumeAnalyticsLoaded && this.riskAnalyticsLoaded);
            },
            error: (error) => {
                if (loadId !== this.dashboardLoadId) return;
                console.error('[BuSelectionComponent] Error loading volume data:', error);
                // Keep existing empty arrays
                this.volumeAnalyticsLoaded = true;
                this.isLoadingAnalytics = !(this.volumeAnalyticsLoaded && this.riskAnalyticsLoaded);
            }
        });
    }

    /**
     * Load risk analytics data for risk charts
     */
    private loadRiskData(): void {
        this.isLoadingAnalytics = true;
        const loadId = this.dashboardLoadId;
        // Only include dates if user has selected them
        const startDate = this.userSelectedFromDate ? this.formatDateForApi(this.fromDate) : '';
        const endDate = this.userSelectedToDate ? this.formatDateForApi(this.toDate) : '';
        const counterparty = this.selectedCounterparty || undefined;

        this.httpService.getDashboardLegalRisk(startDate, endDate, counterparty).subscribe({
            next: (response) => {
                if (loadId !== this.dashboardLoadId) return;
                console.log('[BuSelectionComponent] Dashboard legal risk data loaded successfully');
                
                // Store escalation distribution data
                this.escalationDistribution = response.escalationDistribution;
                
                // Store aging queue data
                this.agingQueue = response.agingQueue;
                
                // Store escalation by counterparty data
                this.escalationByCounterparty = response.escalationByCounterparty;
                
                // Store friction clauses data
                this.frictionClauses = response.frictionClauses;
                
                // Store escalation trend data
                this.escalationTrend = response.escalationTrend;
                
                // Store escalation categories data
                this.escalationCategories = response.escalationCategories;
                this.riskAnalyticsLoaded = true;
                this.isLoadingAnalytics = !(this.volumeAnalyticsLoaded && this.riskAnalyticsLoaded);
            },
            error: (error) => {
                if (loadId !== this.dashboardLoadId) return;
                console.error('[BuSelectionComponent] Error loading risk data:', error);
                // Keep existing empty arrays
                this.riskAnalyticsLoaded = true;
                this.isLoadingAnalytics = !(this.volumeAnalyticsLoaded && this.riskAnalyticsLoaded);
            }
        });
    }

    /**
     * Logout user and redirect to login page
     */
    public logout(): void {
        this.authService.logout();
        this.router.navigate(['/login']);
    }
}
